import {Utils} from "./Utils";
import {BuildingData} from "./move-in-modal";

export interface AutocompleteOptions<T> {
    url?: string | (() => string);
    cacheKey?: string;
    onSelect?: (item: T) => void;
    debounceTimer?: number;
    initialValue?: DataItem<T> | null;
    minQueryLength?: number;
    maxResults?: number;
    cacheTimeout?: number;
    multiSelect?: boolean;
    placeholder?: string;
    noResultsText?: string;
    loadingText?: string;
    typeToSearchText?: string;
    customItemRenderer?: ((item: DataItem<T>) => HTMLElement) | null;
    customFilter?: ((items: DataItem<T>[], query: string) => DataItem<T>[]) | null;
    highlightMatches?: boolean;
    flipUpIfNoSpace?: boolean;
}

export /**
 * Creates a generic item renderer for autocomplete items.
 */
/**
 * Creates a renderer function for an autocomplete list item.
 * @template T The type of the data item.
 * @param {object} config The configuration object for the renderer.
 * @param {(item: T) => string} config.getTitleText Function to get the title text.
 * @param {(item: T) => (string | HTMLElement)} config.getSubtitleText Function to get the subtitle.
 * @param {string | ((item: T) => string)} config.iconName The name of the Material Icon.
 * @param {(item: T) => HTMLElement} [config.getRightElement] Optional function to create an element for the right side.
 * @param {string} [config.containerClassName] Optional class for the main container.
 * @param {string} [config.detailsClassName] Optional class for the details container.
 * @param {string} [config.titleClassName] Optional class for the title.
 * @param {string} [config.subtitleClassName] Optional class for the subtitle.
 * @param {string} [config.iconClassName] Optional class for the icon.
 */
function createItemRenderer<T>(config: {
    getTitleText: (item: T) => string;
    getSubtitleText: (item: T) => (string | HTMLElement);
    iconName: string | ((item: T) => string);
    getRightElement?: (item: T) => HTMLElement;
    containerClassName?: string;
    detailsClassName?: string;
    titleClassName?: string;
    subtitleClassName?: string;
    iconClassName?: string;
}) {
    return (dataItem: DataItem<T>) => {
        const item = dataItem.data;
        if (!item) {
            console.warn('Item data is undefined or null');
            return document.createElement('div');
        }

        // 1. Create the main container element for the item.
        const itemElement = document.createElement('div');
        itemElement.className = config.containerClassName || 'autocomplete-item';

        // 2. Create the icon (now positioned on the left).
        const iconElement = document.createElement('i');
        iconElement.className = config.iconClassName || 'material-icons item-icon';
        iconElement.textContent = typeof config.iconName === 'function' ? config.iconName(item) : config.iconName;

        // 3. Create the container for the item details (title and subtitle).
        const detailsElement = document.createElement('div');
        detailsElement.className = config.detailsClassName || 'item-details';

        // 4. Create and append the title.
        const titleElement = document.createElement('div');
        titleElement.className = config.titleClassName || 'item-title';
        titleElement.textContent = config.getTitleText(item);
        detailsElement.appendChild(titleElement);

        // 5. Create and append the subtitle.
        const subtitleResult = config.getSubtitleText(item);
        const subtitleElement = document.createElement('div');
        subtitleElement.className = config.subtitleClassName || 'item-subtitle';
        if (typeof subtitleResult === 'string') {
            subtitleElement.textContent = subtitleResult;
        } else {
            subtitleElement.appendChild(subtitleResult);
        }
        detailsElement.appendChild(subtitleElement);

        // 6. Assemble the final element structure.
        itemElement.appendChild(iconElement);  // Icon on the left
        itemElement.appendChild(detailsElement); // Details in the middle

        // 👇 Logic to add the element on the right
        // 7. If getRightElement is provided, create and append the element.
        if (config.getRightElement) {
            const rightElement = config.getRightElement(item);
            itemElement.appendChild(rightElement);
        }

        // 8. Return the fully constructed element.
        return itemElement;
    };
}

export interface DataItem<T> {
    text: string;
    idField: string;
    data: T;
    searchableText?: string; // Additional text for searching
}

interface CacheEntry<T> {
    data: DataItem<T>[];
    timestamp: number;
    query: string;
}

export class MaterialAutocomplete<T> {
    private element: HTMLInputElement;
    private options: Required<AutocompleteOptions<T>>;
    private dropdown: Dropdown<T>;
    private cache = new Map<string, CacheEntry<T>>();
    private currentRequest: AbortController | null = null;
    private liveRegion: HTMLElement;
    private selectedItems: DataItem<T>[] = [];
    private isDestroyed = false;
    private boundHandlers: Map<string, EventListener> = new Map();

    set disabled(disabled: boolean) {
        this.element.disabled = disabled;
        if (disabled) {
            this.element.classList.add('m-autocomplete-disabled');
        } else {
            this.element.classList.remove('m-autocomplete-disabled');
        }
    }

    set isLoading(loading: boolean) {
        if (loading) {
            this.element.classList.add('m-autocomplete-loading');
            this.element.setAttribute('aria-busy', 'true');
        } else {
            this.element.classList.remove('m-autocomplete-loading');
            this.element.removeAttribute('aria-busy');
        }
    }

    /**
     * @deprecated
     * @param url The URL string or function returning a string.
     */
    set url(url: string | (() => string)) {
        this.options.url = url;
        if (!(url instanceof Function)) {
            this.element.dataset.url = url; // Update the element's data-url attribute
        }
    }

    get URL(): URL{
        if(this.options.url instanceof Function){
            return new URL(this.options.url(), window.location.origin);
        }else {
            return new URL(this.options.url, window.location.origin);
        }
    }

    get input(){
        return this.element;
    }

    get selectedItem(): DataItem<T> | null{
        if (this.selectedItems.length > 0) {
            return this.selectedItems[0]; // Return the first selected item
        }
        return null; // No items selected
    }

    get selectedData(): T | null {
        if (this.selectedItems.length > 0) {
            return this.selectedItems[0].data; // Return the data of the first selected item
        }
        return null; // No items selected
    }

    set selectedItem(item: DataItem<T> | null) {
        if (item) {
            this.selectedItems = [item]; // Reset selected items to the current selection
            this.element.value = item.text; // Update input value
            this.updateActiveDescendant(item.idField);
            this.options.onSelect(item.data); // Call onSelect callback
        } else {
            this.selectedItems = []; // Clear selected items
            this.element.value = ''; // Clear input value
            this.element.removeAttribute('aria-activedescendant'); // Remove active descendant
        }
        this.dropdown.close(); // Close dropdown after selection
    }

    get selectedItemsList(): DataItem<T>[] {
        return this.selectedItems; // Return the list of selected items
    }

    constructor(element: HTMLInputElement | string, options: Partial<AutocompleteOptions<T>> = {}) {
        if (typeof element === 'string') {
            const found = document.getElementById(element);
            if (!found || !(found instanceof HTMLInputElement)) {
                throw new Error(`MaterialAutocomplete: Element with ID "${element}" not found or not an input element.`);
            }
            this.element = found;
        } else {
            this.element = element;
        }

        if (!this.element) {
            throw new Error("MaterialAutocomplete requires a valid HTMLInputElement or a valid element ID.");
        }

        const defaultUrl = this.element.dataset.url || options.url;
        if (!defaultUrl) {
            throw new Error("Autocomplete requires a URL. Provide it in the element's data-url attribute or in options.");
        }

        this.options = {
            url: defaultUrl,
            cacheKey: options.cacheKey || this.generateRandomCacheKey(),
            onSelect: options.onSelect || (() => {}),
            debounceTimer: options.debounceTimer ?? 300,
            minQueryLength: options.minQueryLength ?? 1,
            maxResults: options.maxResults ?? 50,
            cacheTimeout: options.cacheTimeout ?? 300000, // 5 minutes
            multiSelect: options.multiSelect ?? false,
            placeholder: options.placeholder || "",
            noResultsText: options.noResultsText || "No results found",
            loadingText: options.loadingText || "Loading...",
            typeToSearchText: options.typeToSearchText || "Type to search",
            customItemRenderer: options.customItemRenderer || null,
            customFilter: options.customFilter || null,
            initialValue: options.initialValue || null,
            highlightMatches: options.highlightMatches ?? true,
            flipUpIfNoSpace: options.flipUpIfNoSpace ?? true
        };

        this.liveRegion = this.createLiveRegion();
        this.dropdown = new Dropdown<T>(this.element, {
            onSelect: (item) => this.handleItemSelection(item),
            customRenderer: this.options.customItemRenderer,
            highlightMatches: this.options.highlightMatches,
            flipUpIfNoSpace: this.options.flipUpIfNoSpace,
            noResultsText: this.options.noResultsText,
            loadingText: this.options.loadingText,
            typeToSearchText: this.options.typeToSearchText
        });

        this.initializeA11y();
        this.initializeEvents();

        // Cleanup on page unload
        this.addEventHandler(window, 'beforeunload', () => this.destroy());

        //Setup initial value
        if(options.initialValue) this.selectItem(options.initialValue)
    }

    public update(record: Partial<AutocompleteOptions<T>>): void {
        if (this.isDestroyed) return;
        for (const key in record) {
            if (record.hasOwnProperty(key) && (this.options as any).hasOwnProperty(key)) {
                (this.options as any)[key] = (record as any)[key];
            }
        }
    }

    public reset(){
        if (this.isDestroyed) return;
        this.element.value = '';
        this.selectedItems = [];
        this.dropdown.close();
        this.element.removeAttribute('aria-activedescendant');
        this.element.setAttribute('aria-expanded', 'false');
        this.element.classList.remove('m-autocomplete-open');
        this.liveRegion.textContent = '';
        //Clear cache
        this.cache.clear();
    }

    public async selectItemById(id: string): Promise<void> {
        if (this.isDestroyed) return;
        const cachedItems = Array.from(this.cache.values()).flatMap(entry => entry.data);
        const item = cachedItems.find(i => i.idField === id);
        if (item) {
            this.selectItem(item);
        }else{
            //Use api
            const data: DataItem<T>[] = (await Utils.fetch<T[]>(`${this.options.url}`) as any).data;
            const selectItem = data.find(i => (i.data as any)[i.idField] === parseInt(`${id ?? '0'}`));
            this.selectedItem = selectItem ?? null;
        }
    }

    private generateRandomCacheKey(): string {
        return `autocomplete_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
    }

    private createCacheKey(query: string): string {
        return `${this.options.cacheKey}:${query.toLowerCase()}`;
    }

    private createLiveRegion(): HTMLElement {
        const region = document.createElement('div');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        region.className = 'm-autocomplete-live-region';
        document.body.appendChild(region);
        return region;
    }

    private initializeA11y(): void {
        this.element.setAttribute('role', 'combobox');
        this.element.setAttribute('aria-autocomplete', 'list');
        this.element.setAttribute('aria-haspopup', 'listbox');
        this.element.setAttribute('aria-expanded', 'false');
        this.element.setAttribute('aria-controls', this.dropdown.getId());
        this.element.setAttribute('autocomplete', 'off');

        if (this.element.placeholder !== this.options.placeholder) {
            this.element.placeholder = this.options.placeholder;
        }
    }

    private addEventHandler(target: EventTarget, event: string, handler: EventListener): void {
        target.addEventListener(event, handler);
        this.boundHandlers.set(`${target.constructor.name}_${event}`, handler);
    }

    private initializeEvents(): void {
        this.addEventHandler(this.element, 'focus', () => this.handleFocus());
        this.addEventHandler(this.element, 'blur', (e) => this.handleBlur(e as FocusEvent));
        this.addEventHandler(this.element, 'input', Utils.debounce(() => this.handleInput(), this.options.debounceTimer));
        this.addEventHandler(this.element, 'keydown', (e) => this.handleKeydown(e as KeyboardEvent));
        this.addEventHandler(document, 'click', (e) => this.handleDocumentClick(e as MouseEvent));
    }

    /**
     * MODIFIED: Fetches data immediately on focus.
     */
    private handleFocus(): void {
        if (this.isDestroyed) return;
        this.fetchAndOpen();
    }

    private handleBlur(event: FocusEvent): void {
        if (this.isDestroyed) return;
        const relatedTarget = event.relatedTarget as Element;
        if (relatedTarget && this.dropdown.contains(relatedTarget)) {
            return;
        }
        setTimeout(() => {
            if (!this.dropdown.isBeingInteracted()) {
                this.closeDropdown();
            }
        }, 150);
    }

    public focus(){
        this.input.focus();
    }

    private handleInput(): void {
        if (this.isDestroyed) return;

        const query = this.element.value.trim();
        if (query.length < this.options.minQueryLength && query.length > 0) {
            this.dropdown.showEmptyState('min-length', this.options.minQueryLength);
            this.openDropdown();
            return;
        }

        this.fetchAndOpen();
    }

    private handleKeydown(event: KeyboardEvent): void {
        if (this.isDestroyed) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (!this.dropdown.isOpen()) {
                    this.fetchAndOpen();
                } else {
                    this.updateActiveDescendant(this.dropdown.highlightNext());
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (this.dropdown.isOpen()) {
                    this.updateActiveDescendant(this.dropdown.highlightPrevious());
                }
                break;
            case 'Enter':
                event.preventDefault();
                if (this.dropdown.isOpen()) {
                    this.dropdown.selectHighlighted();
                }
                break;
            case 'Escape':
                this.closeDropdown();
                this.element.focus();
                break;
            case 'Tab':
                this.closeDropdown();
                break;
        }
    }

    private handleDocumentClick(event: MouseEvent): void {
        const target = event.target as Element;
        if (!this.element.contains(target) && !this.dropdown.contains(target)) {
            this.closeDropdown();
        }
    }

    private handleItemSelection(item: DataItem<T>): void {
        if (this.options.multiSelect) {
            if (this.selectedItems.some(selected => selected.idField === item.idField)) {
                // Deselect the item if already selected
                this.selectedItems = this.selectedItems.filter(selected => selected.idField !== item.idField);
                this.element.value = this.selectedItems.map(i => i.text).join(', ');
            } else {
                // Select the item
                this.selectedItems.push(item);
                this.element.value = this.selectedItems.map(i => i.text).join(', ');
            }
            this.options.onSelect(item.data);
            this.updateActiveDescendant(item.idField);
            this.openDropdown();
        } else {
            this.element.value = item.text;
            this.options.onSelect(item.data);
            this.selectedItems = [item]; // Reset selected items to the current selection
            this.closeDropdown();
        }
        this.announceSelection(item);
    }

    private updateActiveDescendant(activeDescendantId: string): void {
        this.element.setAttribute('aria-activedescendant', activeDescendantId);
    }

    private async fetchAndOpen(): Promise<void> {
        if (this.isDestroyed) return;

        const query = this.element.value.trim();
        const cached = this.getCachedData(query);
        if (cached) {
            const filtered = this.applyCustomFilter(cached, query);
            this.dropdown.open(filtered, query);
            this.openDropdown();
            this.announceResults(filtered.length);
            return;
        }

        if (this.currentRequest) this.currentRequest.abort();
        this.dropdown.showLoading();
        this.openDropdown();

        this.currentRequest = new AbortController();
        try {
            const items = await this.fetchData(query, this.currentRequest.signal);
            this.setCachedData(query, items);
            const filtered = this.applyCustomFilter(items, query);
            this.dropdown.open(filtered, query);
            this.announceResults(filtered.length);
        } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
                this.dropdown.showError('Failed to load results.');
            }
        } finally {
            this.currentRequest = null;
        }
    }

    /**
     * Apply the optional customFilter to the fetched items just before display.
     * Runs on display (not before caching) so the cache stays general and the
     * filter always sees the live state (e.g. roles already on the chip list).
     */
    private applyCustomFilter(items: DataItem<T>[], query: string): DataItem<T>[] {
        if (!this.options.customFilter) return items;
        return this.options.customFilter(items, query);
    }

    private getCachedData(query: string): DataItem<T>[] | null {
        const cacheKey = this.createCacheKey(query);
        const cached = this.cache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < this.options.cacheTimeout) {
            return cached.data;
        }
        return null;
    }

    private setCachedData(query: string, data: DataItem<T>[]): void {
        const cacheKey = this.createCacheKey(query);
        this.cache.set(cacheKey, { data, timestamp: Date.now(), query });
        if (this.cache.size > 50) {
            const oldestKey = this.cache.keys().next().value;
            if(oldestKey) this.cache.delete(oldestKey);
        }
    }

    private openDropdown(): void {
        this.element.setAttribute('aria-expanded', 'true');
        this.element.classList.add('m-autocomplete-open');
        this.dropdown.updatePosition();
    }

    private closeDropdown(): void {
        if (!this.dropdown.isOpen()) return;
        this.dropdown.close();
        this.element.setAttribute('aria-expanded', 'false');
        this.element.removeAttribute('aria-activedescendant');
        this.element.classList.remove('m-autocomplete-open');
    }

    private async fetchData(query: string, signal: AbortSignal): Promise<DataItem<T>[]> {
        const url = this.options.url instanceof Function ? new URL(this.options.url(), window.location.origin) :new URL(this.options.url, window.location.origin);
        url.searchParams.set('search', query);
        url.searchParams.set('limit', String(this.options.maxResults));
        url.searchParams.set('cacheKey', this.options.cacheKey);

        const response = await Utils.fetch(url.href, { signal });
        return (response.data || []).map((item: any) => ({
            text: item.text,
            idField: item.idField,
            data: item.data,
            searchableText: item.searchableText || item.text
        }));
    }

    private announceToScreenReader(message: string): void {
        this.liveRegion.textContent = message;
    }

    private announceResults(count: number): void {
        const message = count === 0 ? this.options.noResultsText : `${count} result${count === 1 ? '' : 's'} available`;
        this.announceToScreenReader(message);
    }

    private announceSelection(item: DataItem<T>): void {
        this.announceToScreenReader(`Selected ${item.text}`);
    }

    public destroy(): void {
        if (this.isDestroyed) return;
        this.isDestroyed = true;
        if (this.currentRequest) this.currentRequest.abort();
        this.boundHandlers.forEach((handler, key) => {
            const [targetName, event] = key.split('_');
            // Simplified cleanup - real implementation might need more robust target tracking
        });
        this.dropdown.destroy();
        this.liveRegion.remove();
        this.element.classList.remove('m-autocomplete-open');
        ['role', 'aria-autocomplete', 'aria-haspopup', 'aria-expanded', 'aria-controls'].forEach(attr => this.element.removeAttribute(attr));
    }

    clear() {
        if (this.isDestroyed) return;
        this.element.value = '';
        this.selectedItems = [];
        this.dropdown.close();
        this.element.removeAttribute('aria-activedescendant');
        this.element.setAttribute('aria-expanded', 'false');
        this.element.classList.remove('m-autocomplete-open');
        this.liveRegion.textContent = '';
        this.cache.clear();
    }

    /**
     * Clears the cache and removes all cached data.
     */
    clearCache() {
        if (this.isDestroyed) return;
        this.cache.clear();
        this.announceToScreenReader('Cache cleared');
    }

    /**
     * Selects an item programmatically without calling the onSelect callback.
     * @param item
     */
    selectItem(item: DataItem<T>) {
        if (this.isDestroyed) return;
        this.selectedItems = [item]; // Reset selected items to the current selection
        this.element.value = item.text; // Update input value
        this.updateActiveDescendant(item.idField);
        this.dropdown.close(); // Close dropdown after selection
        this.announceSelection(item); // Announce selection to screen reader
        M.updateTextFields();
    }
}


// --- Dropdown Class (Theme logic removed) ---

interface DropdownOptions<T> {
    onSelect: (item: DataItem<T>) => void;
    customRenderer?: ((item: DataItem<T>) => HTMLElement) | null;
    highlightMatches?: boolean;
    flipUpIfNoSpace?: boolean;
    noResultsText?: string;
    loadingText?: string;
    typeToSearchText?: string;
}

class Dropdown<T> {
    private inputElement: HTMLInputElement;
    private dropdownElement: HTMLDivElement;
    private data: DataItem<T>[] = [];
    private options: Required<DropdownOptions<T>>;
    private highlightedIndex: number = -1;
    private isInteracting: boolean = false;
    private currentQuery: string = '';

    constructor(inputElement: HTMLInputElement, options: DropdownOptions<T>) {
        this.inputElement = inputElement;
        this.options = {
            onSelect: options.onSelect,
            customRenderer: options.customRenderer || null,
            highlightMatches: options.highlightMatches ?? true,
            flipUpIfNoSpace: options.flipUpIfNoSpace ?? true,
            noResultsText: options.noResultsText || 'No results found',
            loadingText: options.loadingText || 'Loading...',
            typeToSearchText: options.typeToSearchText || 'Type to search'
        };
        this.dropdownElement = this.createDropdownElement();
        this.bindEvents();
    }

    private createDropdownElement(): HTMLDivElement {
        const element = document.createElement('div');
        element.classList.add('m-dropdown-content');
        element.id = `${this.inputElement.id || 'autocomplete'}-dropdown`;
        element.setAttribute('role', 'listbox');
        this.inputElement.parentNode?.insertBefore(element, this.inputElement.nextSibling);
        return element;
    }

    private bindEvents(): void {
        this.dropdownElement.addEventListener('mouseenter', () => this.isInteracting = true);
        this.dropdownElement.addEventListener('mouseleave', () => this.isInteracting = false);
        this.dropdownElement.addEventListener('mousemove', (e) => {
            const target = (e.target as Element).closest('[role="option"]');
            if (target) {
                const index = Array.from(this.dropdownElement.children).indexOf(target);
                this.highlightItem(index, false);
            }
        });
    }

    public getId(): string { return this.dropdownElement.id; }
    public isOpen(): boolean { return this.dropdownElement.classList.contains('show'); }
    public isBeingInteracted(): boolean { return this.isInteracting; }
    public contains(element: Element): boolean { return this.dropdownElement.contains(element); }

    public open(items: DataItem<T>[], query: string = ''): void {
        this.currentQuery = query;
        this.populateItems(items);
        this.dropdownElement.classList.add('show');
        this.updatePosition();
    }

    public close(): void {
        this.highlightedIndex = -1;
        this.dropdownElement.classList.remove('show');
        this.dropdownElement.innerHTML = '';
    }

    public showLoading(): void {
        this.dropdownElement.innerHTML = `<div class="dropdown-item-disabled loading"><span class="loading-spinner"></span>${this.options.loadingText}</div>`;
        this.dropdownElement.classList.add('show');
        this.updatePosition();
    }

    public showError(message: string): void {
        this.dropdownElement.innerHTML = `<div class="dropdown-item-disabled error"><span>⚠</span>${message}</div>`;
        this.dropdownElement.classList.add('show');
        this.updatePosition();
    }

    public showEmptyState(type: 'no-results' | 'min-length', minLength?: number): void {
        const message = type === 'no-results' ? this.options.noResultsText : `Type at least ${minLength} characters`;
        this.dropdownElement.innerHTML = `<div class="dropdown-item-disabled">${message}</div>`;
        this.dropdownElement.classList.add('show');
        this.updatePosition();
    }

    public updatePosition(): void {
        const inputRect = this.inputElement.getBoundingClientRect();
        this.dropdownElement.style.minWidth = `${inputRect.width}px`;

        if (this.options.flipUpIfNoSpace) {
            const spaceBelow = window.innerHeight - inputRect.bottom;
            if (spaceBelow < 300 && inputRect.top > spaceBelow) {
                this.dropdownElement.classList.add('flip-up');
            } else {
                this.dropdownElement.classList.remove('flip-up');
            }
        }
    }

    public highlightNext(): string { return this.highlightItem(this.highlightedIndex + 1); }
    public highlightPrevious(): string { return this.highlightItem(this.highlightedIndex - 1); }

    public selectHighlighted(): void {
        if (this.highlightedIndex > -1 && this.data[this.highlightedIndex]) {
            this.options.onSelect(this.data[this.highlightedIndex]);
        }
    }

    private highlightItem(index: number, shouldScroll: boolean = true): string {
        const items = this.dropdownElement.querySelectorAll('[role="option"]');
        if (this.highlightedIndex > -1) items[this.highlightedIndex]?.classList.remove('active');

        if (index >= items.length) index = 0;
        if (index < 0) index = items.length - 1;

        items[index]?.classList.add('active');
        if (shouldScroll) items[index]?.scrollIntoView({ block: 'nearest' });

        this.highlightedIndex = index;
        return items[index]?.id || '';
    }

    private populateItems(items: DataItem<T>[]): void {
        this.data = items;
        this.highlightedIndex = -1;
        this.dropdownElement.innerHTML = '';

        if (items.length === 0) {
            this.showEmptyState('no-results');
            return;
        }

        items.forEach((item, index) => {
            const id = `${this.getId()}-item-${index}`;
            const element = this.options.customRenderer ? this.options.customRenderer(item) : this.createDefaultOption(item);
            element.id = id;
            element.setAttribute('role', 'option');
            element.addEventListener('click', () => this.options.onSelect(item));
            this.dropdownElement.appendChild(element);
        });
    }

    private createDefaultOption(item: DataItem<T>): HTMLElement {
        const element = document.createElement('div');
        element.classList.add('dropdown-item');
        // Blanket gate for every autocomplete without a custom renderer. For ROOMS this is only
        // correct because the server decides `disabled` by comparing occupancy against the room's
        // bed capacity (RoomService.autocomplete) rather than "has any occupant" — a multi-bed room
        // with a free bed must not arrive here already disabled. See
        // prototypes/room-allocation/STEP6_WIRE_RESHAPE_HANDOFF.md.
        if((item.data as any)?.disabled === true){
            element.classList.add('disabled');
        }
        element.innerHTML = this.options.highlightMatches ? this.highlightText(item.text, this.currentQuery) : item.text;
        return element;
    }

    private highlightText(text: string, query: string): string {
        if (!query) return text;
        return text.replace(new RegExp(`(${query})`, 'gi'), '<mark>$1</mark>');
    }

    public destroy(): void {
        this.dropdownElement.remove();
    }
}